import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { format } from "date-fns";
import { ExternalLink, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

async function callInvite(body: Record<string, unknown>) {
  const { error } = await supabase.functions.invoke("admin-invite-sponsor", { body });
  if (!error) return null;
  if (error instanceof FunctionsHttpError) {
    try { const j = await error.context.json(); return j?.error ?? "Request failed"; } catch { return "Request failed"; }
  }
  return error.message;
}

const fmt = (d: string | null) => (d ? format(new Date(d), "MMM d, yyyy h:mm a") : "-");

export function TopShopSponsorsTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const [fullName, setFullName] = useState("");
  const [org, setOrg] = useState("");
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const key = ["admin", "top-shop", "sponsors", programId];
  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await supabase.from("sponsors").select("*").eq("program_id", programId).order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (fullName.trim().length < 2) return setFormError("Full name is required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setFormError("A valid email is required.");
    setInviting(true);
    const err = await callInvite({ action: "invite", email: email.trim(), full_name: fullName.trim(), organization_name: org.trim() || null });
    setInviting(false);
    qc.invalidateQueries({ queryKey: key });
    if (err) { setFormError(err); toast.error(err); return; }
    toast.success("Sponsor invited");
    setFullName(""); setOrg(""); setEmail("");
  };

  const resend = async (s: any) => {
    setBusy(s.id);
    const err = await callInvite({ action: "resend", email: s.email, full_name: s.full_name, organization_name: s.organization_name });
    setBusy(null);
    if (err) toast.error(err); else toast.success("Invite email sent");
    qc.invalidateQueries({ queryKey: key });
  };

  const toggle = async (s: any, v: boolean) => {
    const { error } = await supabase.from("sponsors").update({ is_active: v }).eq("id", s.id);
    if (error) toast.error(error.message); else qc.invalidateQueries({ queryKey: key });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invite sponsor</CardTitle>
          <CardDescription>The sponsor gets an email with a link to set their password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={invite} className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1"><Label htmlFor="sp-name">Full name</Label><Input id="sp-name" value={fullName} onChange={(e) => setFullName(e.target.value)} required /></div>
            <div className="space-y-1"><Label htmlFor="sp-org">Organization</Label><Input id="sp-org" value={org} onChange={(e) => setOrg(e.target.value)} /></div>
            <div className="space-y-1"><Label htmlFor="sp-email">Email</Label><Input id="sp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={inviting}>
                {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Invite sponsor
              </Button>
              {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Sponsors only see accepted participants who agreed to share their details: name, shop name, type of shop, location and current score.</p>
        <Button variant="outline" size="sm" asChild>
          <a href="/sponsor" target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Preview sponsor view</a>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Organization</TableHead><TableHead>Email</TableHead>
                  <TableHead>Invited</TableHead><TableHead>Last email sent</TableHead><TableHead>Active</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sponsors.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No sponsors yet.</TableCell></TableRow>
                ) : sponsors.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.full_name}</TableCell>
                    <TableCell>{s.organization_name ?? "-"}</TableCell>
                    <TableCell>{s.email ?? "-"}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmt(s.invited_at)}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmt(s.last_invite_sent_at)}</TableCell>
                    <TableCell><Switch checked={!!s.is_active} onCheckedChange={(v) => toggle(s, v)} aria-label="Active" /></TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" disabled={!s.email || busy === s.id} onClick={() => resend(s)}>
                        {busy === s.id && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Resend invite
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
